import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { TypographyP2, UserData } from '@heidi/design-system/typography';
import { getInitials } from '@heidi/utils';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { ContextLock } from '../../contracts/projects/work-composer-context-lock';

export type { ContextLock };

export type PhysicalContextCardProps = {
  lock: ContextLock;
  /** Icon actions at the end of the row (Figma "Icons" slot). */
  trailing?: ReactNode;
  /** Extra linked context (e.g. other sessions of the patient) after the lock. */
  extras?: ReactNode;
};

function LockedContextRow({ lock }: { lock: ContextLock }): JSX.Element {
  if (lock.kind === 'patient' && lock.label) {
    return (
      <div
        className="flex min-w-0 items-center gap-1.5"
        data-testid="work-composer-locked-context"
      >
        <TypographyP2 className="shrink-0 text-text-secondary">
          <FormattedMessage defaultMessage="Working with" id="P1C/agnQrF" />
        </TypographyP2>
        <span className="flex min-w-0 items-center gap-1">
          <Avatar size="xs" color={getAvatarColor(lock.label)} aria-hidden="true">
            <AvatarFallback data-testid="work-composer-locked-context-initial">
              {getInitials(lock.label).charAt(0)}
            </AvatarFallback>
          </Avatar>
          <TypographyP2 className="min-w-0 truncate text-text-primary">
            <UserData>{lock.label}</UserData>
          </TypographyP2>
        </span>
      </div>
    );
  }

  if (lock.kind === 'patient') {
    return (
      <div
        className="flex min-w-0 items-center"
        data-testid="work-composer-locked-context"
      >
        <TypographyP2 className="truncate text-text-secondary">
          <FormattedMessage defaultMessage="Working with" id="P1C/agnQrF" />
        </TypographyP2>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center" data-testid="work-composer-locked-context">
      <TypographyP2 className="truncate text-text-secondary">
        <FormattedMessage
          defaultMessage="Working with <target>this session</target>"
          id="5BbPQfNH2z"
          description="Locked Work composer chip when the host session has no linked patient"
          values={{
            target: (chunks: ReactNode) => (
              <span className="text-text-primary">{chunks}</span>
            ),
          }}
        />
      </TypographyP2>
    </div>
  );
}

export function PhysicalContextCard({
  lock,
  trailing,
  extras,
}: PhysicalContextCardProps): JSX.Element {
  return (
    <div
      data-testid="work-composer-physical-context-card"
      // XXX: -32px / 30px have no spacing-token equivalent; they tuck the grey tab behind the composer.
      className="mb-[-32px] flex w-full flex-col rounded-t-2xl border border-border bg-background-tertiary pb-[30px]"
    >
      <div className="flex w-full min-w-0 items-center gap-4 px-5 py-2.5">
        <LockedContextRow lock={lock} />
        {extras ? (
          <div
            className="-my-1 flex min-w-0 flex-wrap items-center gap-1"
            data-testid="work-composer-context-extras"
          >
            {extras}
          </div>
        ) : null}
        {trailing ? (
          // -my-1 keeps a 28px icon button from making the row taller than its text line.
          <div className="-my-1 ms-auto flex shrink-0 items-center gap-1">{trailing}</div>
        ) : null}
      </div>
    </div>
  );
}
